import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Grid,
  Box
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import Button from 'components/Button/button';
import CommonAlert from 'components/CommonAlert/CommonAlert';
import GoogleImageSearcher from 'components/Dialogs/GoogleImageSearcher';
import PaperComponent from 'components/PaperComponent';
import getOptions from 'services/getOptions';
import Environment from 'utils/environments';

const environments = Environment(process.env.REACT_APP_ENV);

function SkusInfoEan({
  open,
  onClose,
  isLoading,
  productData,
  errorMessage,
  eanScanned
}) {
  const userLocal = JSON.parse(localStorage.getItem('companies'));
  const [brands, setBrands] = useState([]);
  const [producers, setProducers] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [unitsMeasurements, setUnitsMeasurements] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageSearchModal, setOpenImageSearchModal] = useState(false);
  const [gisJson, setGisJson] = useState({ sku: { images: {} } });
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');

  const [selectedItems, setSelectedItems] = useState({
    brand: null,
    producer: null,
    packageType: null,
    unit: null
  });

  const isBase64Image = (str) =>
    typeof str === 'string' && str.startsWith('data:image/');

  const mainImage = selectedImage || productData?.data.images?.[0] || null;

  useEffect(() => {
    if (productData?.data) {
      setSelectedItems({
        brand: { id: null, description: productData.data.brand },
        producer: { id: null, description: productData.data.producer },
        packageType: { id: null, description: productData.data.packageType },
        unit: { id: null, abbreviation: productData.data.unit }
      });

      if (productData.data.linkedProducts?.length > 0) {
        setSelectedProductId(productData.data.linkedProducts[0].id);
      }

      if (!products.length) {
        getOptions(
          `${environments.catalog}/products`,
          'products',
          products,
          setProducts,
          null,
          null,
          50
        );
      }
    }
  }, [productData]);

  useEffect(() => {
    const imageFromChat = productData?.data?.images?.[0];

    if (imageFromChat && imageFromChat.startsWith('http')) {
      setSelectedImage(imageFromChat);

      fetch(imageFromChat)
        .then((res) => res.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result;
            setSelectedImage(base64);
          };
          reader.readAsDataURL(blob);
        })
        .catch((err) => {
          console.error('[Erro ao converter imagem do crawler]', err);
        });
    }
  }, [productData]);

  useEffect(() => {
    const base64 = gisJson.sku?.images?.baseImage;
    if (base64) {
      setSelectedImage(base64);
      setOpenImageSearchModal(false);
    }
  }, [gisJson]);

  const handleSaveProduct = async () => {
    try {
      const payload = {
        skuId: productData?.data?.id || undefined,
        ean: eanScanned || productData?.data?.ean,
        name: productData?.data?.name,
        brand: selectedItems.brand?.description || '',
        packageType: selectedItems.packageType?.description || '',
        packageQuantity: productData?.data?.packageQuantity || '',
        unit: selectedItems.unit?.abbreviation || '',
        productId: selectedProductId,
        user: userLocal?.user,
        image: isBase64Image(selectedImage)
          ? selectedImage
          : productData?.data?.images?.[0] || null
      };

      const response = await fetch(
        `${environments.catalog}/skus/gpt`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();

      if (!response.ok) throw new Error(result.message || 'Erro ao salvar SKU');

      setAlertMessage('SKU salvo com sucesso!');
      setAlertSeverity('success');
      setOpenAlert(true);
      onClose();
    } catch (err) {
      setAlertMessage(`Erro ao salvar: ${err.message}`);
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  };

  const handleBack = () => {
    setSelectedItems({
      brand: null,
      producer: null,
      packageType: null,
      unit: null
    });
    setSelectedProductId(null);
    setSelectedImage(null);
    onClose();
  };

  let content;

  if (isLoading) {
    content = (
      <Typography className="textAlignCenter">
        Buscando informações...
      </Typography>
    );
  } else if (errorMessage) {
    content = (
      <Typography className="textAlignCenter" color="error">
        {errorMessage}
      </Typography>
    );
  } else {
    content = (
      <>
        <Typography
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#454A67',
            marginBottom: 4
          }}
        >
          PRODUTO BLOOM
        </Typography>
        <Box
          style={{
            backgroundColor: '#E6E7F0',
            borderRadius: 5,
            paddingTop: 10,
            paddingRight: 4,
            paddingBottom: 4,
            paddingLeft: 4,
            marginBottom: 12,
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)'
          }}
        >
          <Box display="flex" alignItems="center" gap={4}>
            <Box flex={1}>
              <Autocomplete
                style={{ width: '98%' }}
                options={products || []}
                getOptionLabel={(option) =>
                  option?.description?.toUpperCase() || ''
                }
                value={products.find((p) => p.id === selectedProductId) || null}
                onChange={(_, newValue) => {
                  setSelectedProductId(newValue?.id || null);
                }}
                className="autocompleteSmall"
                onFocus={() => {
                  if (!products.length) {
                    getOptions(
                      `${environments.catalog}/products`,
                      'products',
                      products,
                      setProducts,
                      null,
                      null,
                      50
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    className="textFieldSmall backgroundInput"
                  />
                )}
              />
            </Box>
          </Box>

          {productData?.data?.suggestedProducts?.length > 0 && (
            <Typography
              style={{
                fontSize: 12,
                fontWeight: 'bold',
                color: '#2E2E2E',
                marginTop: 12,
                marginBottom: 4
              }}
            >
              SIMILARES
            </Typography>
          )}

          <Box display="flex" flexDirection="column">
            {(productData?.data?.suggestedProducts || []).map(
              (item, index, arr) => (
                <Box
                  key={item.id || index}
                  onClick={() => {
                    console.log('Produto selecionado:', item);
                    setSelectedProductId(item.id);
                  }}
                  style={{
                    paddingBottom: 2,
                    marginBottom: 2,
                    borderBottom:
                      index !== arr.length - 1 ? '1px solid #5062F0' : 'none',
                    cursor: 'pointer',
                    backgroundColor:
                      selectedProductId === item.id ? '#DDE3FF' : 'transparent',
                    padding: '4px 6px',
                    borderRadius: 4
                  }}
                >
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#5062F0',
                      textDecoration: 'none',
                      lineHeight: '20px'
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              )
            )}
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{
            marginBottom: 4
          }}
        >
          <Typography
            style={{ color: '#5062F0', fontSize: 12, fontWeight: 700 }}
          >
            SKU
          </Typography>
          <Typography
            style={{ color: '#5062F0', fontSize: 12, fontWeight: 700 }}
          >
            EAN: {eanScanned || '---'}
          </Typography>
        </Box>

        <Grid
          container
          spacing={1}
          style={{
            backgroundColor: '#7583F0',
            padding: 3,
            paddingTop: 15,
            borderRadius: 5
          }}
        >
          <Grid item xs={12}>
            <TextField
              label="DESCRIÇÃO"
              value={productData?.data.name.toUpperCase() || ''}
              variant="outlined"
              size="small"
              fullWidth
              className="textFieldSmall backgroundInput"
              InputLabelProps={{ shrink: true, style: { color: '#fff' } }}
            />
          </Grid>

          <Grid item xs={6} style={{ paddingRight: 2, paddingTop: 20 }}>
            <Box display="flex" className="itemWithModalSecondary pRelative">
              <Autocomplete
                options={brands || []}
                getOptionLabel={(option) =>
                  option.description?.toUpperCase() || ''
                }
                value={selectedItems.brand}
                onChange={(_, newValue) => {
                  setSelectedItems((prev) => ({ ...prev, brand: newValue }));
                }}
                className="autocompleteSmall"
                onFocus={() => {
                  if (!brands.length) {
                    getOptions(
                      `${environments.catalog}/brands`,
                      'brands',
                      brands,
                      setBrands,
                      null,
                      null,
                      10
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="MARCA"
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true, style: { color: '#fff' } }}
                    className="textFieldSmall backgroundInput"
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={6} style={{ paddingLeft: 2, paddingTop: 20 }}>
            <Box display="flex" className="itemWithModalSecondary pRelative">
              <Autocomplete
                options={producers || []}
                getOptionLabel={(option) =>
                  option.description?.toUpperCase() || ''
                }
                value={selectedItems.producer}
                onChange={(_, newValue) => {
                  setSelectedItems((prev) => ({
                    ...prev,
                    producer: newValue
                  }));
                }}
                className="autocompleteSmall"
                onFocus={() => {
                  if (!producers.length) {
                    getOptions(
                      `${environments.catalog}/producers`,
                      'producers',
                      producers,
                      setProducers,
                      null,
                      null,
                      10
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="FABRICANTE"
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true, style: { color: '#fff' } }}
                    className="textFieldSmall backgroundInput"
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={3} style={{ paddingRight: 2, paddingTop: 20 }}>
            <Box display="flex" className="itemWithModalSecondary pRelative">
              <Autocomplete
                options={packageTypes || []}
                getOptionLabel={(option) =>
                  option.description?.toUpperCase() || ''
                }
                value={selectedItems.packageType}
                onChange={(_, newValue) => {
                  setSelectedItems((prev) => ({
                    ...prev,
                    packageType: newValue
                  }));
                }}
                className="autocompleteSmall"
                onFocus={() => {
                  if (!packageTypes.length) {
                    getOptions(
                      `${environments.catalog}/typePackages`,
                      'packageTypes',
                      packageTypes,
                      setPackageTypes,
                      null,
                      null,
                      10
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="TIPO EMB."
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true, style: { color: '#fff' } }}
                    className="textFieldSmall backgroundInput"
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid
            item
            xs={3}
            style={{ paddingLeft: 2, paddingRight: 2, paddingTop: 20 }}
          >
            <TextField
              label="QTDE"
              value={productData?.data.packageQuantity || ''}
              variant="outlined"
              size="small"
              fullWidth
              className="textFieldSmall backgroundInput"
              InputLabelProps={{ shrink: true, style: { color: '#fff' } }}
              InputProps={{
                style: { height: 35 }
              }}
            />
          </Grid>

          <Grid
            item
            xs={2}
            style={{ paddingLeft: 2, paddingRight: 2, paddingTop: 20 }}
          >
            <Box display="flex" className="itemWithModalSecondary pRelative">
              <Autocomplete
                options={unitsMeasurements || []}
                getOptionLabel={(option) =>
                  option.abbreviation?.toUpperCase() || ''
                }
                value={selectedItems.unit}
                onChange={(_, newValue) => {
                  setSelectedItems((prev) => ({ ...prev, unit: newValue }));
                }}
                className="autocompleteSmall"
                onFocus={() => {
                  if (!unitsMeasurements.length) {
                    getOptions(
                      `${environments.catalog}/unitsMeasurements`,
                      'unitsMeasurements',
                      unitsMeasurements,
                      setUnitsMeasurements,
                      null,
                      null,
                      10
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="UN."
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true, style: { color: '#fff' } }}
                    className="textFieldSmall backgroundInput"
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={4} style={{ paddingLeft: 2, paddingTop: 20 }}>
            <Autocomplete
              options={[]}
              getOptionLabel={(option) => option?.label || ''}
              value={productData?.embalagens || null}
              className="autocompleteSmall"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="EMBALAGENS"
                  variant="outlined"
                  size="small"
                  fullWidth
                  className="textFieldSmall backgroundInput"
                  InputLabelProps={{ shrink: true, style: { color: '#fff' } }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item>
                {mainImage && (
                  <Box
                    style={{
                      width: 94,
                      height: 131,
                      backgroundColor: '#DADDF3',
                      padding: 8,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={mainImage}
                      alt="Imagem principal"
                      style={{
                        width: 78,
                        height: 115,
                        boxShadow: '0 4px 10px #00000080',
                        backgroundColor: '#fff',
                        objectFit: 'contain',
                        border: '1px solid #5062F0',
                        borderRadius: 4
                      }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs>
                <label
                  style={{
                    fontSize: 16,
                    color: '#fff',
                    fontWeight: 'bold',
                    marginBottom: 8,
                    display: 'block'
                  }}
                >
                  IMAGENS
                </label>
                <Grid
                  container
                  spacing={1}
                  style={{
                    overflowX: 'auto',
                    flexWrap: 'nowrap',
                    marginBottom: 10
                  }}
                >
                  {mainImage && (
                    <Grid item>
                      <img
                        src={mainImage}
                        alt="Miniatura principal"
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: 'contain',
                          border: '3px solid #F2B90D',
                          borderRadius: 8,
                          backgroundColor: '#fff'
                        }}
                      />
                    </Grid>
                  )}
                </Grid>

                <Button
                  variant="contained"
                  label="PESQUISAR IMAGEM"
                  className="defaultButton submitButton"
                  style={{
                    width: '50%',
                    height: 50
                  }}
                  onClick={() => setOpenImageSearchModal(true)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
          <Button
            className="defaultButton backButton"
            design="outlined"
            label="VOLTAR"
            onClick={handleBack}
            style={{ outline: 0 }}
          />

          <Button
            className="defaultButton submitButton"
            design="outlined"
            label="CRIAR"
            onClick={handleSaveProduct}
            disabled={!selectedProductId}
            style={{ outline: 0 }}
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        PaperComponent={PaperComponent}
        aria-labelledby="barcode-dialog-title"
      >
        <DialogContent style={{ padding: '10px', marginTop: 64 }}>
          {content}
        </DialogContent>
      </Dialog>

      <GoogleImageSearcher
        openedByGeneralSuggestion={false}
        modalSettings={[
          { name: 'searchImage', open: openImageSearchModal, fullScreen: false }
        ]}
        setModalSettings={(newSettings) => {
          const openState = newSettings.find(
            (m) => m.name === 'searchImage'
          )?.open;
          setOpenImageSearchModal(openState);
        }}
        multipleSkuData={[]}
        setMultipleSkuData={() => {}}
        jsonBody={gisJson}
        setJsonBody={setGisJson}
        providers={[]}
        defaultValue={productData?.data?.name || ''}
        setImgSugg={() => {}}
        setSuggestionsSelected={() => {}}
        suggestionsSelected={{}}
      />

      <CommonAlert
        open={openAlert}
        onClose={() => setOpenAlert(false)}
        messagePersonal={alertMessage}
        severity={alertSeverity}
      />
    </>
  );
}

export default SkusInfoEan;
